import React, { useCallback, useEffect } from "react";
import { Flex, Form, Input, message, Typography } from "antd";

import { SubmitButton } from "@/components/button/submit-button";
import { protectedRequest } from "@/services";
import { CreateUserSchema } from "@/schemas";
import { useLocation, useNavigate } from "react-router-dom";
import _ from "lodash";

const { Text } = Typography;

const onRequestCreateUser = async (
  body: CreateUserSchema,
  callback: () => void
) => {
  try {
    await protectedRequest<CreateUserSchema, any>(
      {
        url: "/user/create",
        method: "POST",
        data: body,
      },
      callback
    );
  } catch (error) {}
};

const onRequestUpdateUser = async (
  id: number,
  body: CreateUserSchema,
  callback: () => void
) => {
  try {
    await protectedRequest<CreateUserSchema, any>(
      {
        url: `/user/update/${id}`,
        method: "POST",
        data: body,
      },
      callback
    );
  } catch (error) {}
};

const CreateUser: React.FC = () => {
  const { state } = useLocation();

  const mode = _.defaultTo(state?.mode, "create");
  const user = _.defaultTo(state?.user, null);

  const [form] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && mode === "update") {
      form.setFieldValue("email", user?.email);
      form.setFieldValue("name", user?.name);
      form.setFieldValue("phone", user?.phone);
      form.setFieldValue("password", user?.password);
      form.setFieldValue("confirmPassword", user?.password);
    }
  }, [form, mode, user, user?.email, user?.name, user?.password]);

  const onCreateOrUpdateUser = useCallback(() => {
    form
      .validateFields()
      .then(async () => {
        const body = {
          email: form.getFieldValue("email"),
          name: form.getFieldValue("name"),
          phone: form.getFieldValue("phone"),
          password: form.getFieldValue("password"),
          imageURL: "",
          roleID: 0,
        };
        try {
          if (mode === "update") {
            await onRequestUpdateUser(user.id, body, () => {
              navigate("/admin/login", { replace: true });
            });
          } else {
            await onRequestCreateUser(body, () => {
              navigate("/admin/login", { replace: true });
            });
          }
          form.resetFields();
          navigate("/admin/user-management");
        } catch (error) {
          console.log(error);
        }
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  }, [form, mode, navigate, user?.id]);

  const onFinish = () => {
    message.success("Submit success!");
  };

  const onFinishFailed = () => {
    message.error("Submit failed!");
  };

  return (
    <Flex gap="middle" vertical>
      <Text style={{ fontSize: 24, color: "#40C1A2" }}>ユーザー作成</Text>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
      >
        <Form.Item
          name="email"
          label="メールアドレス"
          rules={[
            { required: true, message: "メールアドレスを再入力してください" },
            { type: "email", message: "メールは無効です。" },
          ]}
        >
          <Input
            style={{ width: 360 }}
            size="large"
            type="email"
            disabled={mode === "update"}
            placeholder="メールアドレスを入力してください。"
          />
        </Form.Item>

        <Form.Item
          name="name"
          label="名前"
          rules={[
            { required: true, message: "ユーザー名を入力してください。" },
            {
              type: "string",
              min: 8,
              message: "名前は8文字以上である必要があります。",
            },
          ]}
        >
          <Input
            style={{ width: 360 }}
            size="large"
            placeholder="ユーザー名を入力してください。"
          />
        </Form.Item>

        <Form.Item
          name="phone"
          label="電話番号"
          rules={[
            { required: true, message: "電話番号を入力してください。" },
          ]}
        >
          <Input
            style={{ width: 360 }}
            size="large"
            placeholder="電話番号を入力してください。"
          />
        </Form.Item>

        <Form.Item
          name="password"
          label="パスワード"
          rules={[
            { required: true, message: "パスワードを再入力してください。" },
            {
              type: "string",
              min: 8,
              message: "パスワードは8文字以上でなければなりません",
            },
          ]}
        >
          <Input
            style={{ width: 360 }}
            size="large"
            type="password"
            placeholder="パスワードを入力してください。"
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label="パスワードを入力してください。"
          dependencies={["password"]}
          rules={[
            {
              required: true,
              message: "パスワードを再入力してください。",
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error("新しいパスワードが一致しません。")
                );
              },
            }),
          ]}
        >
          <Input
            style={{ width: 360 }}
            size="large"
            type="password"
            placeholder="パスワードを再入力してください。"
          />
        </Form.Item>
      </Form>
      <Form.Item>
        <SubmitButton
          onClick={onCreateOrUpdateUser}
          styles={{ width: 360 }}
          form={form}
          title={mode === "create" ? "ユーザー作成" : "ユーザー更新"}
        />
      </Form.Item>
    </Flex>
  );
};

export default CreateUser;
